import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";

type ToastVariant = "error" | "success" | "info" | "warn";

interface Toast {
  id: number;
  message: string;
  variant: ToastVariant;
}

interface ToastContextValue {
  toast: (message: string, variant?: ToastVariant) => void;
}

const ToastContext = createContext<ToastContextValue>({
  toast: () => {},
});

// useToast gives any component a simple `toast(msg, "error" | "success")`
// function. Replaces silent `console.error` calls in hooks + handlers
// so network failures actually reach the user instead of disappearing.
export function useToast() {
  return useContext(ToastContext);
}

interface ProviderProps {
  children: React.ReactNode;
}

export const ToastProvider: React.FC<ProviderProps> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const toast = useCallback(
    (message: string, variant: ToastVariant = "info") => {
      const id = Date.now() + Math.random();
      setToasts((prev) => [...prev, { id, message, variant }]);
      // Auto-dismiss after 5s for non-error, 8s for errors
      const ttl = variant === "error" ? 8000 : 5000;
      setTimeout(() => dismiss(id), ttl);
    },
    [dismiss]
  );

  // Listen for the custom "parcely:toast" event dispatched by
  // apiClient's interceptors (#120 rate-limit handler). Lets the
  // pure-module apiClient surface toasts without injecting a hook.
  useEffect(() => {
    const handler = (e: Event) => {
      const ce = e as CustomEvent<{ kind?: ToastVariant; message: string; durationMs?: number }>;
      const detail = ce.detail;
      if (!detail || !detail.message) return;
      const id = Date.now() + Math.random();
      const variant = detail.kind || "info";
      setToasts((prev) => [...prev, { id, message: detail.message, variant }]);
      const ttl = detail.durationMs ?? (variant === "error" ? 8000 : 5000);
      setTimeout(() => dismiss(id), ttl);
    };
    window.addEventListener("parcely:toast", handler as EventListener);
    return () => window.removeEventListener("parcely:toast", handler as EventListener);
  }, [dismiss]);

  return (
    <ToastContext.Provider value={{ toast }}>
      {children}
      {toasts.length > 0 && (
        <div
          className="toast-container"
          role="region"
          aria-label="Notifications"
          aria-live="polite"
        >
          {toasts.map((t) => (
            <ToastItem key={t.id} toast={t} onDismiss={() => dismiss(t.id)} />
          ))}
        </div>
      )}
    </ToastContext.Provider>
  );
};

const ToastItem: React.FC<{ toast: Toast; onDismiss: () => void }> = ({
  toast,
  onDismiss,
}) => {
  // Mount animation via a small delay flip
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 10);
    return () => clearTimeout(t);
  }, []);

  return (
    <div
      className={`toast toast--${toast.variant}${visible ? " toast--visible" : ""}`}
      role={toast.variant === "error" ? "alert" : "status"}
    >
      <span className="toast-message">{toast.message}</span>
      <button
        className="toast-dismiss"
        aria-label="Dismiss notification"
        onClick={onDismiss}
      >
        ×
      </button>
    </div>
  );
};
